/**
 * AnimatedLogo — factory simulation platform logo.
 *
 * Three meshing gears (main teal + two cyan side gears), a factory building
 * silhouette, animated conveyor belt, rising smoke puffs, scanning beam,
 * and pulsing circuit nodes.
 *
 * Uses pure SVG SMIL animations for smoke/nodes (no CSS transform issues)
 * and CSS keyframe animations for gear rotation and belt movement.
 *
 * All animation timings are driven by constants so they can be tuned easily.
 */

// ─── Gear path generator ──────────────────────────────────────────────────────
// Generates a gear outline path centered at (cx, cy).
// teeth = number of teeth, ri = inner (root) radius, ro = outer (tip) radius.
// Returns a closed SVG path string.
function makeGearPath(
  cx: number,
  cy: number,
  ri: number,
  ro: number,
  teeth: number,
): string {
  const pitch = (Math.PI * 2) / teeth
  const hw = pitch * 0.32           // tooth half-width = 64 % of pitch is tooth
  const cmds: string[] = []
  const fmt = (v: number) => v.toFixed(2)
  const pt = (r: number, a: number) =>
    `${fmt(cx + r * Math.cos(a))},${fmt(cy + r * Math.sin(a))}`

  for (let i = 0; i < teeth; i++) {
    const base = i * pitch - Math.PI / 2   // start teeth from 12-o'clock
    const cmd = i === 0 ? 'M' : 'L'
    cmds.push(`${cmd}${pt(ri, base - hw)}`)
    cmds.push(`L${pt(ro, base - hw)}`)
    cmds.push(`L${pt(ro, base + hw)}`)
    cmds.push(`L${pt(ri, base + hw)}`)
  }
  return cmds.join(' ') + ' Z'
}

// ─── Pre-computed paths (module-level, computed once) ─────────────────────────
// Main gear: 12 teeth, large — sits in front of factory building centre
const MAIN_GEAR  = makeGearPath(140, 133, 19, 29, 12)
// Side gears: 8 teeth, smaller — mesh with main gear on either side
const LEFT_GEAR  = makeGearPath( 92, 133, 11, 17,  8)
const RIGHT_GEAR = makeGearPath(188, 133, 11, 17,  8)

// ─── Spoke helper ─────────────────────────────────────────────────────────────
function spokePoints(cx: number, cy: number, r1: number, r2: number, n: number) {
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2
    return {
      x1: cx + r1 * Math.cos(a),
      y1: cy + r1 * Math.sin(a),
      x2: cx + r2 * Math.cos(a),
      y2: cy + r2 * Math.sin(a),
    }
  })
}

// ─── Component ────────────────────────────────────────────────────────────────
interface AnimatedLogoProps {
  /** Rendered width in px. Height is derived from aspect ratio 280:200. */
  width?: number
  className?: string
}

export function AnimatedLogo({ width = 280, className }: AnimatedLogoProps) {
  // Gear-to-gear mesh speed: r_main/r_side = 29/17 ≈ 1.706
  const MAIN_DUR  = '8s'
  const SIDE_DUR  = '4.69s'   // 8 / 1.706

  return (
    <>
      {/* ── keyframe animations injected once per page ─────────────────── */}
      <style>{`
        @keyframes lg-cw   { to { transform: rotate( 360deg); } }
        @keyframes lg-ccw  { to { transform: rotate(-360deg); } }
        @keyframes lg-belt { to { stroke-dashoffset: -15; } }
        @keyframes lg-roller-cw { to { transform: rotate(360deg); } }

        /* gear wrappers — transform-box:fill-box centres rotation on the bounding box */
        .lg-main-gear {
          transform-box: fill-box;
          transform-origin: center;
          animation: lg-cw ${MAIN_DUR} linear infinite;
        }
        .lg-side-gear {
          transform-box: fill-box;
          transform-origin: center;
          animation: lg-ccw ${SIDE_DUR} linear infinite;
        }
        .lg-roller {
          transform-box: fill-box;
          transform-origin: center;
          animation: lg-ccw ${SIDE_DUR} linear infinite;
        }
        .lg-belt-top {
          animation: lg-belt 0.85s linear infinite;
        }
        .lg-belt-bot {
          animation: lg-belt 0.85s linear infinite;
        }

        /* main gear glow pulse */
        @keyframes lg-glow {
          0%,100% { filter: drop-shadow(0 0 5px rgba(45,212,191,0.3)); }
          50%     { filter: drop-shadow(0 0 18px rgba(45,212,191,0.85)); }
        }
        .lg-gear-glow { animation: lg-glow 3s ease-in-out infinite; }

        /* factory building faint shimmer */
        @keyframes lg-fac {
          0%,100% { filter: drop-shadow(0 0 3px rgba(45,212,191,0.1)); }
          60%     { filter: drop-shadow(0 0 10px rgba(45,212,191,0.4)); }
        }
        .lg-factory { animation: lg-fac 5s ease-in-out infinite; }

        /* circuit node pulse */
        @keyframes lg-node {
          0%,100% { transform: scale(0.75); opacity: 0.3; }
          50%     { transform: scale(1.55); opacity: 1.0; }
        }
        .lg-node {
          transform-box: fill-box;
          transform-origin: center;
          animation: lg-node 2.3s ease-in-out infinite;
        }
        .lg-node-b { animation-delay: 0.58s; }
        .lg-node-c { animation-delay: 1.15s; }
        .lg-node-d { animation-delay: 1.73s; }

        /* scan beam */
        @keyframes lg-scan {
          0%,4%   { transform: translateY(0px);  opacity: 0;   }
          8%      { opacity: 0.35; }
          92%     { opacity: 0.35; }
          96%,100%{ transform: translateY(72px); opacity: 0;   }
        }
        .lg-scan {
          transform-box: fill-box;
          transform-origin: top left;
          animation: lg-scan 6s ease-in-out infinite;
        }
      `}</style>

      <svg
        viewBox="0 0 280 200"
        width={width}
        height={Math.round(width * (200 / 280))}
        className={className}
        aria-label="FactorySim platform logo"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Subtle radial background glow behind main gear */}
          <radialGradient id="lg-bg" cx="50%" cy="67%" r="38%">
            <stop offset="0%"   stopColor="#2dd4bf" stopOpacity="0.07" />
            <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0"    />
          </radialGradient>

          {/* Node glow filter */}
          <filter id="lg-nglow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Background radial glow */}
        <rect width="280" height="200" fill="url(#lg-bg)" />

        {/* ── Corner circuit traces ─────────────────────────────────────── */}
        {/* Top-left → left chimney */}
        <polyline points="18,18 18,52 65,52" fill="none" stroke="#f59e0b" strokeWidth="0.8" opacity="0.4"/>
        {/* Top-right → right chimney */}
        <polyline points="262,18 262,65 200,65" fill="none" stroke="#10b981" strokeWidth="0.8" opacity="0.4"/>
        {/* Bottom-left → left belt roller */}
        <polyline points="18,182 18,166 58,166" fill="none" stroke="#0891b2" strokeWidth="0.8" opacity="0.4"/>
        {/* Bottom-right → right belt roller */}
        <polyline points="262,182 262,166 222,166" fill="none" stroke="#f59e0b" strokeWidth="0.8" opacity="0.4"/>

        {/* ── Pulsing corner nodes ──────────────────────────────────────── */}
        <circle cx="18"  cy="18"  r="4.5" fill="#f59e0b" className="lg-node"         filter="url(#lg-nglow)"/>
        <circle cx="262" cy="18"  r="4.5" fill="#10b981" className="lg-node lg-node-b" filter="url(#lg-nglow)"/>
        <circle cx="18"  cy="182" r="4.5" fill="#0891b2" className="lg-node lg-node-c" filter="url(#lg-nglow)"/>
        <circle cx="262" cy="182" r="4.5" fill="#f59e0b" className="lg-node lg-node-d" filter="url(#lg-nglow)"/>

        {/* ── Factory building ─────────────────────────────────────────── */}
        <g className="lg-factory">
          {/* Main body */}
          <rect x="48"  y="97"  width="184" height="72" rx="2"
                fill="#0d1117" stroke="#2dd4bf" strokeWidth="1.5"/>
          {/* Left chimney */}
          <rect x="67"  y="49"  width="17"  height="52" rx="1.5"
                fill="#0d1117" stroke="#2dd4bf" strokeWidth="1.5"/>
          {/* Right chimney */}
          <rect x="182" y="61"  width="15"  height="40" rx="1.5"
                fill="#0d1117" stroke="#2dd4bf" strokeWidth="1.5"/>

          {/* Roofline fill-gaps (between chimney and body edges) */}
          <line x1="48"  y1="97" x2="67"  y2="97" stroke="#2dd4bf" strokeWidth="1.5"/>
          <line x1="84"  y1="97" x2="182" y2="97" stroke="#2dd4bf" strokeWidth="1.5"/>
          <line x1="197" y1="97" x2="232" y2="97" stroke="#2dd4bf" strokeWidth="1.5"/>

          {/* Windows */}
          {[56, 88, 148, 180].map((x) => (
            <rect key={x} x={x} y={114} width={22} height={15} rx="1"
                  fill="none" stroke="#334155" strokeWidth="0.9"/>
          ))}
        </g>

        {/* Scan beam (sweeps down the factory building) */}
        <line x1="48" y1="97" x2="232" y2="97"
              stroke="white" strokeWidth="1" opacity="0" className="lg-scan"/>

        {/* ── Smoke puffs: left chimney (3 staggered) ──────────────────── */}
        {[0, 0.7, 1.4].map((delay, i) => (
          <circle key={`ls${i}`} cx="76" cy="46" r="0" fill="#475569" opacity="0">
            <animate attributeName="r"
              values="0;2;4;5;4;2;0" dur="2.1s" begin={`${delay}s`} repeatCount="indefinite"/>
            <animate attributeName="cy"
              values="46;42;37;32;27;23;20" dur="2.1s" begin={`${delay}s`} repeatCount="indefinite"/>
            <animate attributeName="cx"
              values="76;75;77;75;73;76;76" dur="2.1s" begin={`${delay}s`} repeatCount="indefinite"/>
            <animate attributeName="opacity"
              values="0;0.55;0.5;0.35;0.2;0.05;0" dur="2.1s" begin={`${delay}s`} repeatCount="indefinite"/>
          </circle>
        ))}

        {/* ── Smoke puffs: right chimney (2 staggered) ─────────────────── */}
        {[0, 1.0].map((delay, i) => (
          <circle key={`rs${i}`} cx="190" cy="58" r="0" fill="#475569" opacity="0">
            <animate attributeName="r"
              values="0;2;4;4;3;1;0" dur="1.9s" begin={`${delay}s`} repeatCount="indefinite"/>
            <animate attributeName="cy"
              values="58;54;49;44;40;37;35" dur="1.9s" begin={`${delay}s`} repeatCount="indefinite"/>
            <animate attributeName="opacity"
              values="0;0.5;0.4;0.25;0.1;0.03;0" dur="1.9s" begin={`${delay}s`} repeatCount="indefinite"/>
          </circle>
        ))}

        {/* ── Drive shafts (behind gears) ─────────────────────────────── */}
        <line x1="92"  y1="133" x2="140" y2="133" stroke="#162c38" strokeWidth="3.5"/>
        <line x1="140" y1="133" x2="188" y2="133" stroke="#162c38" strokeWidth="3.5"/>

        {/* ── Left side gear ───────────────────────────────────────────── */}
        <g className="lg-side-gear">
          <path d={LEFT_GEAR}  fill="#0c6478" stroke="#2dd4bf" strokeWidth="0.7"/>
          <circle cx="92"  cy="133" r="5" fill="#0d1117" stroke="#2dd4bf" strokeWidth="1"/>
          <circle cx="92"  cy="133" r="2" fill="#2dd4bf"/>
        </g>

        {/* ── Main gear (with glow + spokes) ──────────────────────────── */}
        <g className="lg-gear-glow">
          <g className="lg-main-gear">
            <path d={MAIN_GEAR} fill="#2dd4bf" stroke="#5eead4" strokeWidth="0.6"/>
            {/* 6 spokes */}
            {spokePoints(140, 133, 10, 19, 6).map((s, i) => (
              <line key={i}
                x1={s.x1.toFixed(2)} y1={s.y1.toFixed(2)}
                x2={s.x2.toFixed(2)} y2={s.y2.toFixed(2)}
                stroke="#0d1117" strokeWidth="2.5"/>
            ))}
            <circle cx="140" cy="133" r="9" fill="#0d1117" stroke="#2dd4bf" strokeWidth="1.5"/>
            <circle cx="140" cy="133" r="3.5" fill="#2dd4bf"/>
          </g>
        </g>

        {/* ── Right side gear ──────────────────────────────────────────── */}
        <g className="lg-side-gear">
          <path d={RIGHT_GEAR} fill="#0c6478" stroke="#2dd4bf" strokeWidth="0.7"/>
          <circle cx="188" cy="133" r="5" fill="#0d1117" stroke="#2dd4bf" strokeWidth="1"/>
          <circle cx="188" cy="133" r="2" fill="#2dd4bf"/>
        </g>

        {/* ── Conveyor belt ─────────────────────────────────────────────── */}
        {/* Rollers */}
        <circle cx="58"  cy="165" r="5" fill="none" stroke="#2dd4bf" strokeWidth="1.5" className="lg-roller"/>
        <circle cx="222" cy="165" r="5" fill="none" stroke="#2dd4bf" strokeWidth="1.5" className="lg-roller"/>
        {/* Moving belt lines */}
        <line x1="58"  y1="160" x2="222" y2="160"
              stroke="#2dd4bf" strokeWidth="2" strokeDasharray="10 5" className="lg-belt-top"/>
        <line x1="58"  y1="170" x2="222" y2="170"
              stroke="#0e5568" strokeWidth="1.5" strokeDasharray="10 5" className="lg-belt-bot"/>
      </svg>
    </>
  )
}
